import { formatAll } from 'services/Patients/formatter'
import { Net } from '../Tools'
import { formatSummary, getPatientFromSummary } from './formatter'
import performanceMonitor from 'utils/performanceMonitor'

// Cache for recent requests to avoid duplicate calls
const requestCache = new Map()
const CACHE_DURATION = 5000 // 5 seconds cache
const pendingRequests = new Map() // Track pending requests to avoid duplicates

export const getSummary = async (
  { token, isNew, noLive = false },
  summaryId,
  { summariesDispatch, patientsDispatch } = {},
  log = false
) => {
  if (!summaryId) return false

  const cacheKey = `${summaryId}_${token}`
  const now = Date.now()

  // Check if we have a recent cached result
  const cached = requestCache.get(cacheKey)
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    if (log) console.log('Using cached summary:', summaryId)
    return cached.result
  }

  // Check if there's already a pending request for this summary
  if (pendingRequests.has(cacheKey)) {
    if (log) console.log('Waiting for pending summary request:', summaryId)
    return pendingRequests.get(cacheKey)
  }

  // Create new request
  const requestPromise = (async () => {
    try {
      const res = await Net.get(
        `${process.env.REACT_APP_CLINIC_SERVICE_URL}/GetSummary`,
        token,
        { id: summaryId },
        log
      )

      if (log) console.log('Summary details res: ', res)
      if (typeof res === 'number' || !res) {
        // Cache negative results for a shorter time
        requestCache.set(cacheKey, { result: false, timestamp: now })
        return false
      }

      const formattedSummary = formatSummary(res)
      if (log) console.log('Formatted Summary: ', formattedSummary)

      let result
      if (isNew) {
        const formattedPatient = getPatientFromSummary(res)
        formattedPatient.fromLive = true
        
        patientsDispatch?.({
          type: noLive ? 'addOne' : 'addOneLive',
          payload: formattedPatient,
          log,
        })
        
        summariesDispatch?.({
          type: 'addOne',
          payload: formattedSummary,
          log,
        })
        
        result = [formattedSummary, formattedPatient]
      } else {
        summariesDispatch?.({
          type: 'update',
          payload: {
            summaryId,
            data: formattedSummary,
          },
          log,
        })
        
        result = true
      }

      // Cache the successful result
      requestCache.set(cacheKey, { result, timestamp: now })
      return result

    } catch (error) {
      // Don't cache errors, but log them
      if (log) console.error('Error fetching summary:', summaryId, error)
      throw error
    } finally {
      // Remove from pending requests
      pendingRequests.delete(cacheKey)
    }
  })()

  // Track the pending request
  pendingRequests.set(cacheKey, requestPromise)
  
  return requestPromise
}

// Clean up old cache entries periodically
setInterval(() => {
  const now = Date.now()
  Array.from(requestCache.entries()).forEach(([key, value]) => {
    if (now - value.timestamp > CACHE_DURATION * 2) {
      requestCache.delete(key)
    }
  })
}, CACHE_DURATION * 2)

export const getPatientSummaries = async (
  { token },
  { patientId },
  { summariesDispatch, appointmentsDispatch },
  log = false
) => {
  const startTime = Date.now()
  const res = await Net.get(
    `${process.env.REACT_APP_CLINIC_SERVICE_URL}/ListRegistrations`,
    token,
    { patient_account_id: patientId, without_old_closed_summaries: false },
    log
  )
  
  // Track performance
  performanceMonitor.trackApiCall('ListRegistrations', Date.now() - startTime)
  
  if (typeof res === 'number') return false
  if (log) console.log('Get Summaries Res: ', res)
  // const formattedSummaries = multiple(formatSummary, res, { id: patientId });
  const [patients, summaries, appointments] = formatAll(res)
  if (log) console.log('Formatted Summaries: ', summaries)
  // eslint-disable-next-line no-unused-expressions
  summariesDispatch?.({
    type: 'addBatch',
    payload: summaries,
    log,
  })
  // eslint-disable-next-line no-unused-expressions
  appointmentsDispatch?.({
    type: 'addBatch',
    payload: appointments,
    log,
  })
  return true
}

export const update = async ({ token }, summaryId, params, summariesDispatch, log = true) => {
  const res = await Net.patch(
    `${process.env.REACT_APP_CLINIC_SERVICE_URL}/UpdateSummary`,
    token,
    { ...params, id: summaryId },
    log
  )
  if (typeof res === 'number') return false
  const formattedSummary = formatSummary(res)
  // eslint-disable-next-line no-unused-expressions
  summariesDispatch?.({
    type: 'update',
    payload: {
      summaryId,
      data: formattedSummary,
    },
    log,
  })
  return true
}

export const terminate = async ({ token }, summaryId, summariesDispatch, log = false) => {
  const res = await Net.post(
    `${process.env.REACT_APP_CLINIC_SERVICE_URL}/CloseSummary`,
    token,
    { id: summaryId },
    log
  )
  if (typeof res === 'number') return false
  const formattedSummary = formatSummary(res)
  if (log) console.log('Terminated summary: ', summaryId, res)
  // eslint-disable-next-line no-unused-expressions
  summariesDispatch?.({
    type: 'update',
    payload: {
      summaryId,
      data: formattedSummary,
    },
  })
  return true
}
